// src/components/Navbar.js
import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  AppBar, Toolbar, Button, Box, IconButton, Drawer, List, ListItem,
  ListItemButton, ListItemText, Menu, MenuItem, Divider, Chip
} from '@mui/material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import BuildIcon from '@mui/icons-material/Build';
import SchoolIcon from '@mui/icons-material/School';
import SettingsIcon from '@mui/icons-material/Settings';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ComputerIcon from '@mui/icons-material/Computer';
import SecurityIcon from '@mui/icons-material/Security';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import EngineeringIcon from '@mui/icons-material/Engineering';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import logoSrc from '../images/LogoVector.webp';
import LanguageSwitcher from './LanguageSwitcher';

// Iconos para cada solución
const solutionIcons = {
  techsupport: <BuildIcon fontSize="small" />,
  reliability: <EngineeringIcon fontSize="small" />,
  academy: <SchoolIcon fontSize="small" />,
  processlab: <SettingsIcon fontSize="small" />,
  supplychain: <LocalShippingIcon fontSize="small" />,
  automation: <PrecisionManufacturingIcon fontSize="small" />,
  it: <ComputerIcon fontSize="small" />,
  esd: <SecurityIcon fontSize="small" />,
};

function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { isLoggedIn, logout, ready, user } = useAuth();
  const isAdmin = !!user && user.role === 'admin';

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  
  // Referencias para el timeout
  const closeTimeoutRef = useRef(null);

  // Detectar scroll
  useEffect(() => {
    let frameId = null;

    const handleScroll = () => {
      if (frameId !== null) return;

      frameId = window.requestAnimationFrame(() => {
        const nextScrolled = window.scrollY > 20;
        setScrolled((current) => current === nextScrolled ? current : nextScrolled);
        frameId = null;
      });
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (frameId !== null) window.cancelAnimationFrame(frameId);
    };
  }, []);

  // Limpiar timeout al desmontar
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const handleMenuOpen = (event) => {
    // Cancelar cualquier cierre pendiente
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    // Delay pequeño para permitir movimiento entre botón y menú
    closeTimeoutRef.current = setTimeout(() => {
      setAnchorEl(null);
    }, 100);
  };

  const handleMenuEnter = () => {
    // Cancelar cierre si el mouse entra al menú
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleNavigate = (path) => navigate(path);
  const handleDrawerToggle = () => setDrawerOpen(!drawerOpen);

  const solutionsLinks = useMemo(() => [
    { text: t('about.units.list.techsupport.title'), path: '/solutions/techsupport', icon: solutionIcons.techsupport },
    { text: t('about.units.list.reliability.title'), path: '/solutions/reliability', icon: solutionIcons.reliability },
    { text: t('about.units.list.academy.title'), path: '/solutions/academy', icon: solutionIcons.academy },
    { text: t('about.units.list.processlab.title'), path: '/solutions/processlab', icon: solutionIcons.processlab },
    { text: t('about.units.list.supplychain.title'), path: '/solutions/supplychain', icon: solutionIcons.supplychain },
    { text: t('about.units.list.automation.title'), path: '/solutions/automation', icon: solutionIcons.automation },
    { text: t('about.units.list.it.title'), path: '/solutions/it', icon: solutionIcons.it },
    { text: t('about.units.list.esd.title'), path: '/solutions/esd', icon: solutionIcons.esd },
  ], [t]);

  const navLinks = useMemo(() => {
    const base = [
      { text: t('nav.home'), path: '/' },
      { text: t('nav.services'), path: '/services', menuItems: solutionsLinks },
      { text: t('nav.about'), path: '/about-us' },
      { text: t('nav.forum'), path: '/forum' },
      { text: t('nav.contact'), path: '/contact', highlight: true },
    ];
    if (ready && isAdmin) base.push({ text: 'Dashboard', path: '/admin' });
    return base;
  }, [ready, isAdmin, t, solutionsLinks]);

  const isActive = (path) => location.pathname === path;

  const menuItems = (
    <>
      {navLinks.map((link) =>
        link.menuItems ? (
          <Box 
            key={link.text} 
            onMouseLeave={handleMenuClose}
          >
            <Button
              onClick={() => handleNavigate(link.path)}
              onMouseEnter={handleMenuOpen}
              endIcon={<ArrowDropDownIcon sx={{ 
                transition: 'transform 0.3s ease',
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)'
              }} />}
              sx={{
                fontWeight: 600,
                color: isActive(link.path) ? '#1565C0' : 'text.primary',
                position: 'relative',
                px: 2,
                py: 1,
                transition: 'all 0.3s ease',
                '&:hover': {
                  color: '#1565C0',
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: isActive(link.path) ? '60%' : '0%',
                  height: '3px',
                  bgcolor: '#1565C0',
                  borderRadius: '3px 3px 0 0',
                  transition: 'width 0.3s ease',
                },
                '&:hover::after': {
                  width: '60%',
                }
              }}
            >
              {link.text}
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={() => {
                if (closeTimeoutRef.current) {
                  clearTimeout(closeTimeoutRef.current);
                }
                setAnchorEl(null);
              }}
              disableScrollLock={true}
              MenuListProps={{ 
                onMouseEnter: handleMenuEnter,
                onMouseLeave: handleMenuClose
              }}
              PaperProps={{
                onMouseEnter: handleMenuEnter,
                onMouseLeave: handleMenuClose,
                sx: {
                  background: 'rgba(255, 255, 255, 0.98)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '12px',
                  mt: 0.5,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                  border: '1px solid rgba(255,255,255,0.8)',
                  minWidth: '280px',
                }
              }}
              slotProps={{
                backdrop: {
                  sx: {
                    backgroundColor: 'transparent',
                    pointerEvents: 'none'
                  }
                }
              }}
              sx={{
                pointerEvents: 'none',
                '.MuiPaper-root': {
                  pointerEvents: 'auto',
                },
              }}
            >
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Chip 
                  label="8 Solutions" 
                  size="small" 
                  sx={{ 
                    bgcolor: '#1565C0',
                    color: 'white', 
                    fontWeight: 'bold',
                    fontSize: '0.7rem'
                  }} 
                />
              </Box>
              {link.menuItems.map((item) => (
                <MenuItem
                  key={item.text}
                  component={RouterLink}
                  to={item.path}
                  onClick={() => {
                    if (closeTimeoutRef.current) {
                      clearTimeout(closeTimeoutRef.current);
                    }
                    setAnchorEl(null);
                  }}
                  sx={{
                    py: 1.5,
                    px: 2,
                    gap: 1.5,
                    minHeight: 48,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'rgba(21, 101, 192, 0.08)',
                      pl: 2.5,
                      '& .MuiSvgIcon-root': {
                        color: '#1565C0',
                        transform: 'scale(1.1)',
                      }
                    }
                  }}
                >
                  <Box sx={{ 
                    color: 'text.secondary',
                    transition: 'all 0.2s ease',
                  }}>
                    {item.icon}
                  </Box>
                  <Box sx={{ fontSize: '0.9rem', fontWeight: 500 }}>{item.text}</Box>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        ) : (
          <Button
            key={link.text}
            component={RouterLink}
            to={link.path}
            variant={link.highlight ? 'contained' : 'text'}
            sx={{
              fontWeight: 600,
              color: link.highlight ? 'white' : (isActive(link.path) ? '#1565C0' : 'text.primary'),
              bgcolor: link.highlight ? '#1565C0' : 'transparent',
              position: 'relative',
              px: link.highlight ? 3 : 2,
              py: 1,
              borderRadius: link.highlight ? '24px' : '8px',
              transition: 'all 0.3s ease',
              boxShadow: link.highlight ? '0 4px 12px rgba(21, 101, 192, 0.3)' : 'none',
              '&:hover': {
                color: link.highlight ? 'white' : '#1565C0',
                bgcolor: link.highlight ? '#0D47A1' : 'transparent',
                transform: 'translateY(-2px)',
                boxShadow: link.highlight ? '0 6px 16px rgba(21, 101, 192, 0.4)' : 'none',
              },
              '&::after': !link.highlight && {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: isActive(link.path) ? '60%' : '0%',
                height: '3px',
                bgcolor: '#1565C0',
                borderRadius: '3px 3px 0 0',
                transition: 'width 0.3s ease',
              },
              '&:hover::after': !link.highlight && {
                width: '60%',
              }
            }}
          >
            {link.text}
          </Button>
        )
      )}
      {isLoggedIn ? (
        <Button 
          onClick={logout} 
          sx={{ 
            fontWeight: 600, 
            color: 'text.primary',
            transition: 'all 0.3s ease',
            '&:hover': {
              color: 'error.main',
              transform: 'translateY(-2px)',
            }
          }}
        >
          {t('nav.logout')}
        </Button>
      ) : (
        <Button 
          component={RouterLink} 
          to="/login" 
          variant="outlined"
          sx={{ 
            fontWeight: 600,
            borderRadius: '24px',
            borderColor: '#1565C0',
            color: '#1565C0',
            px: 3,
            transition: 'all 0.3s ease',
            '&:hover': {
              borderColor: '#0D47A1',
              bgcolor: 'rgba(21, 101, 192, 0.08)',
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }
          }}
        >
          {t('nav.login')}
        </Button>
      )}
      <LanguageSwitcher />
    </>
  );

  // ... resto del código (drawer, etc.) permanece igual
  
  const drawer = (
    <Box
      sx={{ 
        textAlign: 'center', 
        width: 280, 
        bgcolor: 'background.paper', 
        height: '100%', 
        color: 'text.primary' 
      }}
    >
      <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box
          component="img"
          src={logoSrc}
          alt="SMTLINK Logo"
          width={150}
          height={45}
          decoding="async"
          sx={{ height: 45, width: 'auto', mb: 2 }}
        />
        <Box onClick={(e) => e.stopPropagation()}>
          <LanguageSwitcher />
        </Box>
      </Box>

      <List sx={{ px: 1 }}>
        {navLinks.map((link) => (
          <ListItem key={link.text} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton 
              component={RouterLink} 
              to={link.path || '#'}
              selected={isActive(link.path)}
              onClick={handleDrawerToggle}
              sx={{
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&.Mui-selected': {
                  bgcolor: 'rgba(21, 101, 192, 0.12)',
                  color: '#1565C0',
                  fontWeight: 700,
                },
                '&:hover': {
                  bgcolor: 'rgba(21, 101, 192, 0.08)',
                  transform: 'translateX(4px)',
                }
              }}
            >
              <ListItemText 
                primary={link.text} 
                primaryTypographyProps={{ 
                  fontWeight: isActive(link.path) ? 700 : 600 
                }} 
              />
            </ListItemButton>
          </ListItem>
        ))}

        <Divider sx={{ my: 2 }} />

        {isLoggedIn ? (
          <ListItem disablePadding>
            <ListItemButton 
              onClick={() => {
                logout();
                handleDrawerToggle();
              }}
              sx={{
                borderRadius: 2,
                color: 'error.main',
                '&:hover': {
                  bgcolor: 'rgba(211, 47, 47, 0.08)',
                }
              }}
            >
              <ListItemText 
                primary={t('nav.logout')} 
                primaryTypographyProps={{ fontWeight: 600 }} 
              />
            </ListItemButton>
          </ListItem>
        ) : (
          <ListItem disablePadding>
            <ListItemButton 
              component={RouterLink} 
              to="/login"
              onClick={handleDrawerToggle}
              sx={{
                borderRadius: 2,
                bgcolor: '#1565C0',
                color: 'white',
                '&:hover': {
                  bgcolor: '#0D47A1',
                }
              }}
            >
              <ListItemText 
                primary={t('nav.login')} 
                primaryTypographyProps={{ fontWeight: 700 }} 
              />
            </ListItemButton>
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar 
        position="sticky" 
        elevation={scrolled ? 3 : 1} 
        sx={{ 
          background: scrolled ? 'rgba(255, 255, 255, 0.98)' : '#ffffff',
          backdropFilter: scrolled ? 'blur(10px)' : 'none',
          transition: 'all 0.3s ease',
          borderBottom: scrolled ? '1px solid rgba(0,0,0,0.08)' : 'none',
        }}
      >
        <Toolbar sx={{ py: 0.5 }}>
          <RouterLink to="/" style={{ display: 'flex', alignItems: 'center' }}>
            <Box 
              component="img" 
              src={logoSrc} 
              alt="SMTLINK Logo" 
              width={184}
              height={55}
              decoding="async"
              sx={{ 
                height: 55, 
                width: 'auto',
                mr: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05) rotate(-2deg)',
                }
              }} 
            />
          </RouterLink>
          <Box sx={{ flexGrow: 1 }} />
          {isMobile ? (
            <IconButton 
              aria-label="open drawer" 
              edge="end" 
              onClick={handleDrawerToggle} 
              sx={{ 
                color: 'text.primary',
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: 'rgba(21, 101, 192, 0.08)',
                  transform: 'rotate(90deg)',
                }
              }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>{menuItems}</Box>
          )}
        </Toolbar>
      </AppBar>
      <Drawer 
        anchor="right" 
        open={drawerOpen} 
        onClose={handleDrawerToggle}
        PaperProps={{
          sx: {
            boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
          }
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
}

export default Navbar;
